import React, { useEffect, useState, useRef } from 'react';
import { Card, CardContent, Typography, Box, LinearProgress, Chip, Avatar, Skeleton, Alert } from '@mui/material';
import { Title, useTranslate } from 'react-admin';
import {
    ElectricBolt,
    BatteryChargingFull,
    AttachMoney,
    Bolt,
    ArrowUpward,
    ArrowDownward,
    LocationOn,
    CheckCircle,
    Error as ErrorIcon,
    OfflineBolt,
    HelpOutline,
    EvStation,
    Business,
} from '@mui/icons-material';
import { usePublicSettings } from '../hooks/usePublicSettings';
import { apiClient } from '../utils/apiClient';

// Types for dashboard stats
interface DashboardStats {
    total_sites: number;
    total_chargers: number;
    today: {
        revenue: number;
        sessions: number;
        energy_kwh: number;
        active_sessions: number;
    };
    yesterday: {
        revenue: number;
        sessions: number;
        energy_kwh: number;
    };
    charger_status: {
        total: number;
        available: number;
        charging: number;
        offline: number;
        faulted: number;
        other: number;
    };
    recent_sessions: Array<{
        id: string;
        site_name: string;
        charger_name: string;
        duration_minutes: number;
        energy_kwh: number;
        revenue: number;
        completed_at: string;
    }>;
}

// Helper to calculate percentage change
const calculateChange = (today: number, yesterday: number): { value: string; trend: 'up' | 'down' | 'neutral' } => {
    if (yesterday === 0) {
        if (today > 0) return { value: '+100%', trend: 'up' };
        return { value: '0%', trend: 'neutral' };
    }
    const change = ((today - yesterday) / yesterday) * 100;
    const rounded = Math.round(change);
    if (rounded > 0) return { value: `+${rounded}%`, trend: 'up' };
    if (rounded < 0) return { value: `${rounded}%`, trend: 'down' };
    return { value: '0%', trend: 'neutral' };
};

// Helper to format currency
const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'HKD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
};

// Helper to format time ago
const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
};

export const Dashboard = () => {
    const translate = useTranslate();
    const { settings: companySettings } = usePublicSettings();
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const isInitialLoadRef = useRef(true);

    useEffect(() => {
        const fetchDashboardStats = async () => {
            try {
                // Only show loading skeleton on initial load, not on refreshes
                if (isInitialLoadRef.current) {
                    setLoading(true);
                }
                setError(null);
                const response = await apiClient.get<{ data: DashboardStats }>('/api/backend/dashboard-stats');
                setStats(response.data);
            } catch (err: any) {
                console.error('Failed to fetch dashboard stats:', err);
                setError(err.message || 'Failed to load dashboard data');
            } finally {
                setLoading(false);
                isInitialLoadRef.current = false;
            }
        };

        fetchDashboardStats();

        // Refresh every 60 seconds
        const interval = setInterval(fetchDashboardStats, 60000);
        return () => clearInterval(interval);
    }, []);

    // Calculate stats for display
    const revenueChange = stats ? calculateChange(stats.today.revenue, stats.yesterday.revenue) : { value: '0%', trend: 'neutral' as const };
    const sessionsChange = stats ? calculateChange(stats.today.sessions, stats.yesterday.sessions) : { value: '0%', trend: 'neutral' as const };
    const energyChange = stats ? calculateChange(stats.today.energy_kwh, stats.yesterday.energy_kwh) : { value: '0%', trend: 'neutral' as const };

    const statCards = [
        {
            title: translate('dashboard.stats.totalSites'),
            value: stats?.total_sites.toString() || '0',
            change: translate('dashboard.stats.accessible'),
            trend: 'neutral' as const,
            icon: Business,
            color: '#6366F1',
        },
        {
            title: translate('dashboard.stats.totalChargers'),
            value: stats?.total_chargers.toString() || '0',
            change: translate('dashboard.stats.accessible'),
            trend: 'neutral' as const,
            icon: EvStation,
            color: '#8B5CF6',
        },
        {
            title: translate('dashboard.stats.todayRevenue'),
            value: stats ? formatCurrency(stats.today.revenue) : '$0',
            change: revenueChange.value,
            trend: revenueChange.trend,
            icon: AttachMoney,
            color: '#009A75',
        },
        {
            title: translate('dashboard.stats.sessionsToday'),
            value: stats?.today.sessions.toString() || '0',
            change: sessionsChange.value,
            trend: sessionsChange.trend,
            icon: BatteryChargingFull,
            color: '#2196F3',
        },
        {
            title: translate('dashboard.stats.energyDelivered'),
            value: stats ? `${stats.today.energy_kwh.toFixed(1)} kWh` : '0 kWh',
            change: energyChange.value,
            trend: energyChange.trend,
            icon: Bolt,
            color: '#FF6B6B',
        },
        {
            title: translate('dashboard.stats.activeSessions'),
            value: stats?.today.active_sessions.toString() || '0',
            change: translate('dashboard.stats.liveNow'),
            trend: 'neutral' as const,
            icon: ElectricBolt,
            color: '#FFB800',
        },
    ];

    // Charger status for the progress bar
    const chargerStatus = stats?.charger_status || { total: 0, available: 0, charging: 0, offline: 0, faulted: 0, other: 0 };
    const onlineChargers = chargerStatus.available + chargerStatus.charging + chargerStatus.other;
    const healthPercentage = chargerStatus.total > 0 ? Math.round((onlineChargers / chargerStatus.total) * 100) : 0;

    return (
        <>
            <Title title={translate('dashboard.title')} />
            <Box sx={{ backgroundColor: 'transparent', minHeight: 'calc(100vh - 120px)', maxWidth: '100%' }}>
                {/* Header */}
                <Box sx={{ mb: { xs: 2, sm: 4 } }}>
                    <Typography sx={{ fontWeight: 700, color: '#1A1A1A', mb: 1, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                        {translate('dashboard.title')}
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#606060' }}>
                        {translate('dashboard.welcome', { companyName: companySettings.companyName })}
                    </Typography>
                </Box>

                {/* Error Alert */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {/* Stats Cards */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(auto-fit, minmax(280px, 1fr))' }, gap: { xs: 2, sm: 3 }, mb: 4 }}>
                    {statCards.map((stat) => {
                        const Icon = stat.icon;
                        return (
                            <Card
                                key={stat.title}
                                sx={{
                                    position: 'relative',
                                    overflow: 'hidden',
                                    minWidth: 0,
                                    border: 'none',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: 4,
                                        background: `linear-gradient(90deg, ${stat.color} 0%, ${stat.color}88 100%)`,
                                        borderRadius: '20px 20px 0 0',
                                    },
                                }}
                            >
                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography variant="body2" sx={{ color: '#606060', mb: 1, fontWeight: 500, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                {stat.title}
                                            </Typography>
                                            {loading ? (
                                                <Skeleton variant="text" width={100} height={48} />
                                            ) : (
                                                <Typography sx={{ fontWeight: 700, color: '#1A1A1A', mb: 1, fontSize: { xs: '1.1rem', sm: '1.75rem', md: '2.5rem' }, lineHeight: 1.2, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                                    {stat.value}
                                                </Typography>
                                            )}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                                {stat.trend === 'up' && (
                                                    <ArrowUpward sx={{ fontSize: 16, color: '#009A75' }} />
                                                )}
                                                {stat.trend === 'down' && (
                                                    <ArrowDownward sx={{ fontSize: 16, color: '#FF3D00' }} />
                                                )}
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: stat.trend === 'up' ? '#009A75' : stat.trend === 'down' ? '#FF3D00' : '#606060',
                                                        fontWeight: 600,
                                                        fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                                    }}
                                                >
                                                    {stat.change}
                                                </Typography>
                                                {stat.trend !== 'neutral' && (
                                                    <Typography variant="body2" sx={{ color: '#909090', ml: 0.5, fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                                                        {translate('dashboard.vsYesterday')}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                        <Box
                                            sx={{
                                                width: { xs: 36, sm: 56 },
                                                height: { xs: 36, sm: 56 },
                                                borderRadius: 2,
                                                backgroundColor: `${stat.color}15`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                                ml: 1,
                                            }}
                                        >
                                            <Icon sx={{ fontSize: { xs: 20, sm: 28 }, color: stat.color }} />
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        );
                    })}
                </Box>

                {/* Charger Status Card */}
                <Card sx={{ mb: 4, overflow: 'hidden' }}>
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1A1A1A', mb: { xs: 2, sm: 3 }, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                            {translate('dashboard.chargerStatus.title')}
                        </Typography>

                        {loading ? (
                            <Skeleton variant="rectangular" height={100} />
                        ) : (
                            <>
                                {/* Status Summary Row */}
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(auto-fit, minmax(150px, auto))' }, gap: { xs: 1.5, sm: 3 }, mb: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CheckCircle sx={{ color: '#009A75', fontSize: 20 }} />
                                        <Typography variant="body2" sx={{ color: '#606060' }}>
                                            {translate('dashboard.chargerStatus.available')}:
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                            {chargerStatus.available}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <ElectricBolt sx={{ color: '#2196F3', fontSize: 20 }} />
                                        <Typography variant="body2" sx={{ color: '#606060' }}>
                                            {translate('dashboard.chargerStatus.charging')}:
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                            {chargerStatus.charging}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <OfflineBolt sx={{ color: '#909090', fontSize: 20 }} />
                                        <Typography variant="body2" sx={{ color: '#606060' }}>
                                            {translate('dashboard.chargerStatus.offline')}:
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                            {chargerStatus.offline}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <ErrorIcon sx={{ color: '#FF3D00', fontSize: 20 }} />
                                        <Typography variant="body2" sx={{ color: '#606060' }}>
                                            {translate('dashboard.chargerStatus.faulted')}:
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                            {chargerStatus.faulted}
                                        </Typography>
                                    </Box>
                                    {chargerStatus.other > 0 && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <HelpOutline sx={{ color: '#FFB800', fontSize: 20 }} />
                                            <Typography variant="body2" sx={{ color: '#606060' }}>
                                                {translate('dashboard.chargerStatus.other')}:
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                {chargerStatus.other}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>

                                {/* Health Progress Bar */}
                                <Box sx={{ mb: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="body2" sx={{ color: '#606060' }}>
                                            {translate('dashboard.chargerStatus.networkHealth')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1A1A1A' }}>
                                            {onlineChargers}/{chargerStatus.total}
                                        </Typography>
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={healthPercentage}
                                        sx={{
                                            height: 8,
                                            borderRadius: 4,
                                            backgroundColor: '#F0F0F0',
                                            '& .MuiLinearProgress-bar': {
                                                backgroundColor: healthPercentage >= 90 ? '#009A75' : healthPercentage >= 70 ? '#FFB800' : '#FF3D00',
                                                borderRadius: 4,
                                            },
                                        }}
                                    />
                                </Box>
                                {(chargerStatus.offline > 0 || chargerStatus.faulted > 0) && (
                                    <Typography variant="body2" sx={{ color: '#909090', mt: 1 }}>
                                        {chargerStatus.offline + chargerStatus.faulted} {translate('dashboard.chargerStatus.needsAttention')}
                                    </Typography>
                                )}
                            </>
                        )}
                    </CardContent>
                </Card>

                {/* Recent Charging Sessions */}
                <Card sx={{ mb: 4, overflow: 'hidden' }}>
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1A1A1A', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('dashboard.recentSessions.title')}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#606060' }}>
                                    {translate('dashboard.recentSessions.subtitle')}
                                </Typography>
                            </Box>
                        </Box>

                        {loading ? (
                            <Box>
                                {[1, 2, 3].map((i) => (
                                    <Skeleton key={i} variant="rectangular" height={60} sx={{ mb: 1, borderRadius: 1 }} />
                                ))}
                            </Box>
                        ) : stats?.recent_sessions.length === 0 ? (
                            <Typography variant="body2" sx={{ color: '#909090', textAlign: 'center', py: 4 }}>
                                {translate('dashboard.recentSessions.noSessions')}
                            </Typography>
                        ) : (
                            <Box>
                                {stats?.recent_sessions.map((session, index) => (
                                    <Box
                                        key={session.id}
                                        sx={{
                                            py: { xs: 1.5, sm: 2 },
                                            borderBottom: index < (stats?.recent_sessions.length || 0) - 1 ? '1px solid #F0F0F0' : 'none',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, mb: { xs: 1, sm: 0 } }}>
                                            <Avatar sx={{ bgcolor: '#009A7515', color: '#009A75', width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}>
                                                <LocationOn sx={{ fontSize: { xs: 18, sm: 24 } }} />
                                            </Avatar>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#1A1A1A', fontSize: { xs: '0.875rem', sm: '1rem' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {session.site_name}
                                                    </Typography>
                                                    <Chip
                                                        label={formatTimeAgo(session.completed_at)}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: '#F0F0F0',
                                                            color: '#606060',
                                                            fontWeight: 500,
                                                            fontSize: '0.75rem',
                                                            height: 24,
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                </Box>
                                                <Typography variant="body2" sx={{ color: '#606060', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                    {session.charger_name}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: { xs: 2, sm: 3 }, ml: { xs: '44px', sm: '56px' } }}>
                                            <Box>
                                                <Typography variant="body2" sx={{ color: '#909090', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                    {translate('dashboard.recentSessions.duration')}
                                                </Typography>
                                                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                                                    {session.duration_minutes}m
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" sx={{ color: '#909090', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                    {translate('dashboard.recentSessions.energy')}
                                                </Typography>
                                                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                                                    {session.energy_kwh} kWh
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" sx={{ color: '#909090', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                    {translate('dashboard.recentSessions.revenue')}
                                                </Typography>
                                                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                                                    ${session.revenue}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Box>
        </>
    );
};
